import { ErrorMessage, Field, Form as FormikForm, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FaPlus } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import axios from '../../admin-axios';
import useActions from '../../hooks/useActions';
import { detectError } from '../../utils';
import LongPaginationHandler from '../LongPaginationHandler';
import { SuccessModal } from '../Modals';
import CustomerTableRow from './CustomerTableRow';
import classes from './index.module.scss';

const initialValues = {
  firstname: '',
  lastname: '',
  phone: '',
  email: '',
  customer_type: '',
  total_gas_purchased: '',
  total_amount: '',
  total_debt: '',
  performance: ''
};
const phoneRules =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
const validationSchema = Yup.object({
  firstname: Yup.string().required('Required'),
  lastname: Yup.string().required('Required'),
  phone: Yup.string()
    .min(11, 'phone number should be 11 chars')
    .max(11, 'Phone number should be 11 chars')
    .matches(phoneRules, { message: 'please enter a valid phone number' })
    .required(' Phone number is required'),
  email: Yup.string().email('email is not valid').required('Required'),
  customer_type: Yup.string().required('Required'),
  total_gas_purchased: Yup.number('Value must be a number')
    .typeError('You must specify a number')
    .positive('Must be a positive number.')
    .required('Required'),
  total_amount: Yup.number('Value must be a number')
    .typeError('You must specify a number')
    .positive('Must be a positive number.')
    .required('Required'),
  total_debt: Yup.number('Value must be a number')
    .typeError('You must specify a number')
    .positive('Must be a positive number.')
    .required('Required'),
  performance: Yup.string().required('Required')
});

const CustomersTable = ({ match }) => {
  const { searchCustomers, getRetailers, getEndUsers, setCustomerView } = useActions();
  const searchRef = useRef();
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const {
    user: { stationId, id: userId }
  } = useSelector((state) => state.auth);

  const {
    customerView,
    retailers,
    retailersPage,
    retailersLoading,
    retailersError,
    retailersPerPage,
    retailersTotal,
    endUsers,
    endUsersPage,
    endUsersPerPage,
    endUsersTotal,
    endUsersLoading,
    endUsersError,
    searchResults,
    searchResultsLoading,
    searchResultsError
  } = useSelector((state) => state.customers);

  const handleFormClose = () => setShowForm(false);
  const handleFormShow = () => setShowForm(true);

  const paginate = (pageNumber) => {
    if (customerView === 'retailers') {
      getRetailers(stationId, pageNumber);
    } else {
      getEndUsers(stationId, pageNumber);
    }
  };

  const searchHandler = (event) => {
    if (event.code === 'Enter') {
      setCustomerView('searching');
      searchCustomers(stationId, searchRef.current.value);
      searchRef.current.value = '';
    }
  };

  useEffect(() => {
    if (customerView === 'retailers') {
      if (!retailers) {
        getRetailers(stationId, retailersPage);
      }
    } else if (customerView === 'endusers') {
      if (!endUsers) {
        getEndUsers(stationId, endUsersPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerView]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <div className={classes.customers}>
        <div className="d-flex justify-content-between mb-3">
          <div className={classes.customers_buttons}>
            <button
              className="me-3"
              onClick={() => setCustomerView('retailers')}
              style={{
                backgroundColor: customerView === 'retailers' ? '#000000' : '#F0F0F0',
                // backgroundColor: customerView === 'retailers' ? '#989111' : '#F0F0F0',
                color: customerView === 'retailers' ? '#FFF' : '#636060'
              }}
              type="button">
              Retailer
            </button>
            <button
              className=""
              onClick={() => setCustomerView('endusers')}
              style={{
                backgroundColor: customerView === 'endusers' ? '#000000' : '#F0F0F0',
                color: customerView === 'endusers' ? '#FFF' : '#636060'
              }}
              type="button">
              End Users
            </button>
          </div>
          <div className="">
            <button
              type="button"
              onClick={handleFormShow}
              className={`btn p-3 ${classes.customers_register_button}`}>
              <FaPlus /> Register Existing Customer
            </button>
          </div>
        </div>
        <div className={`${classes.customers_search} mb-3`}>
          <input
            type="text"
            ref={searchRef}
            onKeyDown={searchHandler}
            className={`form-control p-3 ${classes.customers_search_input}`}
            placeholder="Search by Customer name, Phone number"
            aria-label="search"
          />
        </div>
        <div className={classes.customers_table_wrapper}>
          <table className={`table table-striped ${classes.customers_table}`}>
            <thead>
              <tr>
                <th scope="col">Customer Name</th>
                <th scope="col">Phone No.</th>
                {/* <th scope="col">Performance</th> */}
                <th scope="col">Action</th>
              </tr>
            </thead>
            {customerView === 'searching' && (
              <CustomerTableRow
                view={customerView}
                loading={searchResultsLoading}
                results={searchResults}
                error={searchResultsError}
                path={match.url}
                btnClass={classes.customers_table_button}
              />
            )}
            {customerView === 'retailers' && (
              <CustomerTableRow
                view={customerView}
                loading={retailersLoading}
                results={retailers}
                error={retailersError}
                path={match.url}
                btnClass={classes.customers_table_button}
              />
            )}
            {customerView === 'endusers' && (
              <CustomerTableRow
                view={customerView}
                loading={endUsersLoading}
                results={endUsers}
                error={endUsersError}
                path={match.url}
                btnClass={classes.customers_table_button}
              />
            )}
          </table>
        </div>
        {customerView === 'retailers' && (
          <LongPaginationHandler
            elementsPerPage={retailersPerPage}
            totalElements={retailersTotal}
            activePage={retailersPage}
            paginate={paginate}
          />
        )}
        {customerView === 'endusers' && (
          <LongPaginationHandler
            elementsPerPage={endUsersPerPage}
            totalElements={endUsersTotal}
            activePage={endUsersPage}
            paginate={paginate}
          />
        )}
      </div>
      <div className="customers_modals">
        <Modal show={showForm} onHide={handleFormClose}>
          <Modal.Header closeButton>
            <Modal.Title className={classes.customers_modals_form_title}>
              <span>Register Existing Customer</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                try {
                  setFormLoading(true);
                  // Register existing customer

                  const toSend = {
                    ...values,
                    created_by: userId,
                    station_id: stationId
                  };

                  // console.log(toSend);
                  await axios.post(`/customer/existing/add/${userId}`, toSend);

                  setFormLoading(false);
                  setShowSuccess(true);
                  handleFormClose();
                  if (values.customer_type === 'retailer') {
                    getRetailers(stationId, retailersPage);
                  } else {
                    getEndUsers(stationId, endUsersPage);
                  }
                  // getCustomers(stationId, customers_page);
                } catch (error) {
                  // console.log(error);
                  const message = detectError(error);
                  setFormLoading(false);
                  toast.error(message);
                }
              }}>
              {(formik) => {
                return (
                  <FormikForm className="mb-5">
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="firstname" className="fw-bold">
                        First Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="First Name"
                        name="firstname"
                        id="firstname"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="firstname" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="lastname" className="fw-bold">
                        Last Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Last Name"
                        name="lastname"
                        id="lastname"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="lastname" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="phonenumber" className="fw-bold">
                        Phone Number
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Phone Number"
                        name="phone"
                        id="phonenumber"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="phone" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="email" className="fw-bold">
                        Email
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Email"
                        name="email"
                        id="email"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="email" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="fw-bold">Customer Type</label>
                      <Field as="select" className="form-select" name="customer_type">
                        <option value="">Options</option>
                        <option value="retailer">Retailer</option>
                        <option value="end user">End User</option>
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="customer_type"
                      />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="total_gas_purchased" className="fw-bold">
                        Total Gas Quantity Purchased(KG)
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Total Gas Quantity Purchased"
                        name="total_gas_purchased"
                        id="total_gas_purchased"
                      />
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="total_gas_purchased"
                      />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="total_amount" className="fw-bold">
                        Total Amount Paid
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Total Amount Paid"
                        name="total_amount"
                        id="total_amount"
                      />
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="total_amount"
                      />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="total_debt" className="fw-bold">
                        Total Debt
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Total Debt"
                        name="total_debt"
                        id="total_debt"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="total_debt" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="fw-bold">Performance</label>
                      <Field as="select" className="form-select" name="performance">
                        <option value="">Options</option>
                        {/* <option value="high">High</option> */}
                        {/* <option value="average">Average</option> */}
                        <option value="low">Low</option>
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="performance"
                      />
                    </div>
                    <div className="mb-2">
                      {/* {error && <p>{error}</p>} */}
                      {formLoading ? (
                        <div className="text-center d-flex justify-content-center w-100 h-100">
                          <Spinner animation="border" variant="dark" />
                        </div>
                      ) : (
                        <Button
                          disabled={!formik.isValid}
                          className={`w-100 ${classes.customers_modals_form_button}`}
                          variant="primary"
                          type="submit">
                          Register Existing Customer
                        </Button>
                      )}
                    </div>
                  </FormikForm>
                );
              }}
            </Formik>
          </Modal.Body>
        </Modal>
        <SuccessModal
          message={"You've successfully registered an existing customer"}
          showSuccess={showSuccess}
          setShowSuccess={setShowSuccess}
        />
      </div>
    </>
  );
};

export default CustomersTable;
